<template>
  <div id="seatmap-app" :class="'mode-' + mode">
    <template v-if="renderer">
      <Camera v-if="!app.props.disablePanZoom"></Camera>
      <ContextMenu v-if="showMenu && selection" :selection="selection" />
      <Renderer />
      <Tooltip></Tooltip>
    </template>
    <template v-else>
      <TopBar></TopBar>
      <template v-if="isLayoutSelected && seatmapLoaded">
        <Camera></Camera>
        <ContextMenu v-if="showMenu && selection && !shiftKey" :selection="selection" />
        <Stats v-else></Stats>
        <BGMenu v-if="isBackgroundMode"></BGMenu>
      </template>
      <MOTD v-if="!isLayoutSelected && seatmapLoaded"></MOTD>
      <StatusBar />
      <div v-if="notification" :class="`bottom-notification ${notificationType}`">
        <span v-html="notification"></span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import Camera from '@/components/camera/Camera.vue';
import ContextMenu from '@/components/contextmenu/ContextMenu.vue';
import MOTD from '@/components/motd/MOTD.vue';
import { CATEGORY_TYPES, RENDERER_MODE } from '@/constants';
import { useActions, useGetters, useState } from '@/store/useStore';
import eventbus from 'tiny-emitter/instance';
import { computed, onBeforeUnmount, onMounted, ref, render, shallowRef, watch } from 'vue';
import Application from './core/application';
import forOwn from 'lodash/forOwn';
import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';
import { GA } from '@/core/models/ga/ga';
import { Category } from './store/modules/categories';
import { TableSeat } from './core/models/tables/tableSeat';
import Renderer from '@/components/stats/renderer.vue';
import Tooltip from '@/components/Tooltip.vue';
import TopBar from '@/components/topbar/TopBar.vue';
import Stats from '@/components/stats/Stats.vue';
import BGMenu from '@/components/bgmenu/BGMenu.vue';
import StatusBar from './components/StatusBar.vue';

const app = Application.instance;

const { mode, layout, selectionEnabled, shiftKey } = useState('settings');
const { showMenu, showLabels, showLocks, notificationType, notification } = useState('seatmap');
const { isBackgroundMode, isLayoutSelected } = useGetters('settings');

const seatmapLoaded = ref(false);
const bgMenuRef = ref<InstanceType<typeof BGMenu>>();

eventbus.on('seatmap:loaded', () => {
  seatmapLoaded.value = true;
});

const { showContextMenu, hideContextMenu, setShowLocks } = useActions('seatmap');

const selection = shallowRef<Selection>();
const renderer = computed(() => RENDERER_MODE);

watch(selectionEnabled, (value) => {
  app.onSelectionModeChanged(value);
});

watch(mode, (newValue, oldValue) => {
  eventbus.emit('seatmap:mode:changed', newValue);
  app.svg.removeClass(`mode-${oldValue}`).addClass(`mode-${newValue}`);
});

watch(layout, (value) => {
  eventbus.emit('layout:changed', value);
});

watch(showLabels, (value) => {
  eventbus.emit(`seatmap:labels:${value ? 'show' : 'hide'}`);
  app.svg.toggleClass('show-labels');
});

watch(showLocks, (value) => {
  eventbus.emit(`seatmap:lock:${value ? 'show' : 'hide'}`);
  app.svg.toggleClass('show-locks');
});

// Methods
const showSelectionMenu = (newSelection: Selection) => {
  selection.value = newSelection;
  showContextMenu();
};

const onLockShow = () => {
  setShowLocks(true);
};

const onLockHide = () => {
  setShowLocks(false);
};

onMounted(() => {
  eventbus.on('menu:show', showSelectionMenu);
  eventbus.on('menu:hide', hideContextMenu);
  eventbus.on('seatmap:lock:show', onLockShow);
  eventbus.on('seatmap:lock:hide', onLockHide);

  if (!RENDERER_MODE) {
    eventbus.on('category:set', onCategoryUpdate);
    eventbus.on('seatmap:labels:updated', onCategoryUpdate);
    eventbus.on('seatmap:mode:changed', onCategoryUpdate);
    eventbus.on('seatmap:updated', onCategoryUpdate);
    eventbus.on('seatmap:exitZone:after', onCategoryUpdate);
    eventbus.on('seatmap:categories:updated', onCategoryUpdate);
    eventbus.on('seatmap:add:block', onCategoryUpdate);
    eventbus.on('refreshUnassignedCount', onCategoryUpdate);
    eventbus.on('layout:changed', onCategoryUpdate);

    eventbus.on('seatmap:labels:updated', checkLabelIcons);
    eventbus.on('seatmap:mode:changed', checkLabelIcons);
    eventbus.on('seatmap:lock:show', checkLabelIcons);
    eventbus.on('seatmap:lock:hide', checkLabelIcons);
    eventbus.on('seatmap:labels:show', checkLabelIcons);
    eventbus.on('seatmap:labels:hide', checkLabelIcons);
  }
});

onBeforeUnmount(() => {
  eventbus.off('menu:show', showSelectionMenu);
  eventbus.off('menu:hide', hideContextMenu);
  eventbus.off('seatmap:lock:show', onLockShow);
  eventbus.off('seatmap:lock:hide', onLockHide);

  if (!RENDERER_MODE) {
    eventbus.off('category:set', onCategoryUpdate);
    eventbus.off('seatmap:labels:updated', onCategoryUpdate);
    eventbus.off('seatmap:mode:changed', onCategoryUpdate);
    eventbus.off('seatmap:updated', onCategoryUpdate);
    eventbus.off('seatmap:exitZone:after', onCategoryUpdate);
    eventbus.off('seatmap:categories:updated', onCategoryUpdate);
    eventbus.off('seatmap:add:block', onCategoryUpdate);
    eventbus.off('refreshUnassignedCount', onCategoryUpdate);
    eventbus.off('layout:changed', onCategoryUpdate);

    eventbus.off('seatmap:labels:updated', checkLabelIcons);
    eventbus.off('seatmap:mode:changed', checkLabelIcons);
    eventbus.off('seatmap:lock:show', checkLabelIcons);
    eventbus.off('seatmap:lock:hide', checkLabelIcons);
    eventbus.off('seatmap:labels:show', checkLabelIcons);
    eventbus.off('seatmap:labels:hide', checkLabelIcons);
  }
});

const { updateAssignedList, changeUnassignedCount, changeUnassignedGaCount } = useActions('categories');

const { assignedList, categoryCollection } = useState('categories');
const { isInsideZoneLayout } = useGetters('settings');

const elsCount = ref(0);

const onCategoryUpdate = () => {
  const newAssignedList = getNewAssignedList();
  updateAssignedList(newAssignedList);
  const newUnassignedCount = getUnassignedCount();
  app.unassigned = newUnassignedCount;
  changeUnassignedCount(newUnassignedCount);
  const newUnassignedGaCount = getUnassignedGaCount();
  app.unassignedGa = newUnassignedGaCount;
  changeUnassignedGaCount(newUnassignedGaCount);
};
const getUnassignedCount = () => {
  let assignedCount = 0;
  assignedList.value.forEach((p, idx) => {
    if (idx !== undefined) {
      assignedCount += p.category.type !== CATEGORY_TYPES.GA ? p.count : 0;
    }
  });
  return elsCount.value - assignedCount;
};
const getUnassignedGaCount = () => {
  let unassignedGa = 0;
  const filteredGa = app.getFilteredGa(isInsideZoneLayout.value);
  if (filteredGa) {
    const gaGroupedByCategory = groupBy(filteredGa, (el) => el.category);
    if (gaGroupedByCategory && gaGroupedByCategory.undefined) {
      unassignedGa = gaGroupedByCategory.undefined.length;
    }
  }
  return unassignedGa;
};

const getNewAssignedList = () => {
  app.refreshCategoryList(false);
  const seatsAndTableSeats = app.seatmap.getVisibleSeats(app.currentSection?.uuid);
  const gaList: GA[] = app.getFilteredGa(isInsideZoneLayout.value);
  elsCount.value = seatsAndTableSeats.length;
  const elsGroupedByCategory = groupBy(seatsAndTableSeats, (el) => el.categoryId);
  const newAssignedList: Array<{ category: Category; count: number }> = [];
  forOwn(elsGroupedByCategory, (els, _categoryId) => {
    const categoryId = Number(_categoryId);
    if (categoryId !== undefined && categoryId !== 0) {
      if (Number.isNaN(categoryId) || !categoryCollection.value) {
        return; //filter undefined and null
      }
      const findCategory = categoryCollection.value.find((p) => Number(p.id) === categoryId);
      if (findCategory) {
        newAssignedList.push({
          category: findCategory,
          count: els.length,
        });
      }
    }
  });
  gaList.forEach((st) => {
    const { categoryId } = st.attributes;
    if (!categoryId || !categoryCollection.value) {
      return;
    }
    const foundCategory = newAssignedList.find((p) => p.category.id === categoryId);
    if (foundCategory) {
      foundCategory.count += st.seatsCount;
    } else {
      const cat = categoryCollection.value.find((p) => p.id === categoryId);
      if (cat) {
        newAssignedList.push({
          category: categoryCollection.value.find((p) => p.id === categoryId)!,
          count: st.seatsCount,
        });
      }
    }
  });
  return newAssignedList;
};

const checkLabelIcons = () => {
  const allSeats = app.getFilteredSeats(app.currentSection?.uuid);

  const isTableSeat = (seat: any): seat is TableSeat => !!seat.table;

  const tables = uniqBy(allSeats.filter(isTableSeat), 'uuid').map((s) => s.table);

  for (const table of tables) {
    if (!app.showLabels && app.seatmap.currentMode !== 'labels') {
      table.hideTableLabel();
      continue;
    }

    if (app.currentSection?.isInside && !isInsideZoneLayout.value) {
      table.hideTableLabel();
      continue;
    }

    table.showTableLabel();
  }

  for (let sIndex = 0; sIndex < allSeats.length; sIndex++) {
    const seat = allSeats[sIndex];

    if (!app.showLabels && app.seatmap.currentMode !== 'labels') {
      seat.hideLabel();
      continue;
    }

    if (app.currentSection?.isInside && !isInsideZoneLayout.value) {
      seat.hideLabel();
      continue;
    }

    if (seat.locked && app.showLocks) {
      seat.hideLabel();
      continue;
    }

    if ((seat.reserved || seat.inCart) && app.seatmap.currentMode !== 'labels') {
      seat.hideLabel();
      continue;
    }

    seat.showLabel();
  }
};
</script>

<style lang="scss">
#seatmap-app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  // text-align: center;
  color: #2c3e50;
  z-index: 50;
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
}
.no-selection {
  -webkit-user-select: none; /* webkit (safari, chrome) browsers */
  -moz-user-select: none; /* mozilla browsers */
  -khtml-user-select: none; /* webkit (konqueror) browsers */
  -ms-user-select: none; /* IE10+ */
  user-select: none;
}
.bottom-notification {
  text-align: center;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 13px 35px;
  font-size: 1.2em;
  background: #eaeaea;
  color: #ffffff;
  box-shadow: 3px 0px 11px 3px #aeaeae;
  transition: 0.5s ease all;
  z-index: 55;
  &.success {
    background: #1ab394;
  }
  &.error {
    background: #d32f2f;
  }

  ul {
    width: auto;
    margin: auto;
    display: inline-block;
    list-style-type: disc;
    text-align: left;
    white-space: nowrap;
  }
}
</style>
